import { useEffect, useRef, type ComponentProps, type ReactNode } from "react";
import Select, { type GroupBase, type Props } from "react-select";
import { useFormField } from "@/components/ui/form";
import { AutoComplete } from "@/components/ui/autocomplete-search";

/**
 * Controls that sit inside a FormField and need to be reachable from its label.
 *
 * The form field gives its label `htmlFor` an id and the shadcn FormControl hands that same id
 * to its child. A react-select puts that id on its outer box rather than on the input a person
 * focuses, and the shared autocomplete takes no id at all, so the label pointed at nothing.
 * These wrappers put the id on the real input.
 */

export function LinkedSelect<
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: Props<Option, IsMulti, Group>) {
  const { formItemId, formDescriptionId, formMessageId, error } = useFormField();
  // FormControl also passes `id`, which would land on the outer box: the label's id goes on the input instead.
  const { id: _ignored, ...rest } = props as Props<Option, IsMulti, Group> & { id?: string };
  void _ignored;
  return (
    <Select<Option, IsMulti, Group>
      {...rest}
      inputId={formItemId}
      aria-invalid={!!error}
      aria-describedby={error ? `${formDescriptionId} ${formMessageId}` : formDescriptionId}
    />
  );
}

/** Wraps a control that renders its own text input so the form label is tied to it. */
export function LinkedInput({ children }: { children: ReactNode }) {
  const { formItemId, error } = useFormField();
  const box = useRef<HTMLDivElement>(null);
  // Tie the label to the input once, and drop a list reference that points at nothing.
  useEffect(() => {
    const input = box.current?.querySelector("input");
    if (!input) return;
    input.id = formItemId;
    const controls = input.getAttribute("aria-controls");
    if (controls && !document.getElementById(controls)) input.removeAttribute("aria-controls");
  }, [formItemId]);
  useEffect(() => {
    box.current?.querySelector("input")?.setAttribute("aria-invalid", String(!!error));
  }, [error]);
  return (
    <div ref={box} className="contents">
      {children}
    </div>
  );
}

/** The shared autocomplete, with its form label tied to its text box. */
export function LinkedAutoComplete(props: ComponentProps<typeof AutoComplete>) {
  return (
    <LinkedInput>
      <AutoComplete {...props} />
    </LinkedInput>
  );
}
